import {
  FocusContext,
  setFocus,
} from '@noriginmedia/norigin-spatial-navigation'
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  formatContentRating,
  secondsToTTS,
  truncate,
} from '../../../../shared/helpers'
import { useHybridFocusable } from '../../hooks/useHybridFocusable'
import { useAppSelector } from '../../hooks/useStore'
import tts from '../../lib/TTS'
import TextModal from '../TextModal'
import ContentMetadata from './ContentMetadata'
import ContentButtons, {
  CONTENT_BUTTON_FOCUS_KEY,
} from './buttons/ContentButtons'
import type { ContentDetailsProps } from './types'

const CONTENT_DETAILS_FOCUS_KEY = 'CONTENT_DETAILS'

function ContentDetails(props: ContentDetailsProps) {
  const { onFocus } = props

  const { ref, focusKey } = useHybridFocusable({
    focusKey: CONTENT_DETAILS_FOCUS_KEY,
    onFocus,
  })
  const [toggleFullDescription, setToggleFullDescription] = useState(false)
  const isInitialMountRef = useRef(true)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { id, videoDetails } = useAppSelector((state) => state.content)

  const {
    showTitle,
    episodeTitle,
    content_rating: contentRating,
    captionTracks,
    duration,
    description_long: descriptionLong,
    formattedOrdinals,
  } = videoDetails

  const ttsIntroduction = () => {
    if (!id) return ''

    const hasCaptions = !!(captionTracks && captionTracks[0])

    return `
      ${showTitle}.
      ${episodeTitle}.
      ${formattedOrdinals}.
      ${secondsToTTS(duration)}.
      ${formatContentRating(contentRating)}.
      ${hasCaptions ? 'closed captions available.' : ''}
      ${truncate.apply(descriptionLong, [100, true])}.
        `
  }

  useEffect(() => {
    if (!toggleFullDescription && id) {
      tts.clearPushedText()
      tts.pushTextToRead(ttsIntroduction())
      if (isInitialMountRef.current) {
        const INITIAL_MOUNT_FOCUS_DELAY = 100
        timerRef.current = setTimeout(() => {
          setFocus(CONTENT_BUTTON_FOCUS_KEY)
          isInitialMountRef.current = false
        }, INITIAL_MOUNT_FOCUS_DELAY)
      } else {
        setFocus(CONTENT_BUTTON_FOCUS_KEY)
      }
    }

    return () => {
      tts.clearPushedText()
      tts.stopRead()
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [toggleFullDescription, id])

  useEffect(
    () => () => {
      tts.clearPushedText()
      tts.stopRead()
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    },
    []
  )

  const handleDescriptionModal = () => {
    setToggleFullDescription(!toggleFullDescription)
  }

  return (
    <FocusContext.Provider value={focusKey}>
      <div className="video-detail-screen__content-detail" ref={ref}>
        {toggleFullDescription &&
          document.getElementById('app') &&
          createPortal(
            <TextModal
              title={episodeTitle}
              content={descriptionLong}
              handleDescriptionModal={handleDescriptionModal}
            />,
            document.getElementById('app')!
          )}
        <ContentMetadata
          videoDetails={videoDetails}
          handleDescriptionModal={handleDescriptionModal}
        />
        <ContentButtons />
      </div>
    </FocusContext.Provider>
  )
}

export default ContentDetails
